import React, { useEffect, useRef, useState } from "react";

/**
 * "Sign in with Apple" button for the web.
 *
 * Uses Apple's official JS SDK (appleid.auth.js) in popup mode, so no extra npm
 * dependency is required — mirroring how the Google button works. On success the
 * popup returns an `id_token` (Apple identity token) which the parent forwards to
 * `POST /api/v1/auth/apple`.
 *
 * Apple only returns the user's name on their *first* sign-in, so we surface it
 * via `onSuccess(identityToken, fullName?)` for new-account creation.
 *
 * Required Vite env vars:
 *   VITE_APPLE_SERVICE_ID   — the Service ID created in the Apple Developer console
 *   VITE_APPLE_REDIRECT_URI — a return URL registered on that Service ID (https)
 */

const APPLE_SDK_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

const SERVICE_ID = ((import.meta.env.VITE_APPLE_SERVICE_ID as string) || "").trim();
const REDIRECT_URI = ((import.meta.env.VITE_APPLE_REDIRECT_URI as string) || "").trim();

interface AppleAuthResponse {
  authorization: { id_token: string; code: string; state?: string };
  user?: { name?: { firstName?: string; lastName?: string }; email?: string };
}

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: Record<string, unknown>) => void;
        signIn: () => Promise<AppleAuthResponse>;
      };
    };
  }
}

let sdkPromise: Promise<void> | null = null;

function loadAppleSdk(): Promise<void> {
  if (window.AppleID) return Promise.resolve();
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${APPLE_SDK_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load Apple SDK")));
      return;
    }
    const script = document.createElement("script");
    script.src = APPLE_SDK_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Apple SDK"));
    document.head.appendChild(script);
  });
  return sdkPromise;
}

interface Props {
  /** Called with the Apple identity token and (first login only) the user's full name. */
  onSuccess: (identityToken: string, fullName?: string) => void;
  onError: (message: string) => void;
  /** Toggles the button label between sign-in and sign-up wording. */
  mode?: "signin" | "signup";
  disabled?: boolean;
}

const AppleSignInButton: React.FC<Props> = ({ onSuccess, onError, mode = "signin", disabled }) => {
  const [ready, setReady] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (!SERVICE_ID || !REDIRECT_URI) {
      // Not configured — component renders nothing.
      return;
    }
    let cancelled = false;
    loadAppleSdk()
      .then(() => {
        if (cancelled || !window.AppleID) return;
        if (!initialized.current) {
          window.AppleID.auth.init({
            clientId: SERVICE_ID,
            scope: "name email",
            redirectURI: REDIRECT_URI,
            usePopup: true,
            state: crypto.randomUUID(),
            nonce: crypto.randomUUID(),
          });
          initialized.current = true;
        }
        setReady(true);
      })
      .catch((err) => {
        console.error("[AppleSignIn] SDK load/init failed:", err);
        if (!cancelled) onError("Apple sign-in is unavailable. Try again later.");
      });
    return () => {
      cancelled = true;
    };
  }, [onError]);

  if (!SERVICE_ID || !REDIRECT_URI) return null;

  // Non-async handler so Safari preserves the user-gesture chain and opens a
  // popup instead of falling back to redirect mode (new tab).
  const handleClick = () => {
    if (!window.AppleID) {
      onError("Apple sign-in is not ready yet. Try again.");
      return;
    }
    window.AppleID.auth.signIn().then((res) => {
      const idToken = res?.authorization?.id_token;
      if (!idToken) {
        onError("Apple sign-in failed. Try again.");
        return;
      }
      const first = res.user?.name?.firstName ?? "";
      const last = res.user?.name?.lastName ?? "";
      const fullName = `${first} ${last}`.trim() || undefined;
      onSuccess(idToken, fullName);
    }).catch((err: unknown) => {
      // The user closing the popup raises a `popup_closed_by_user` error — ignore it.
      const code = (err as { error?: string } | null)?.error;
      if (code === "popup_closed_by_user" || code === "user_cancelled_authorize") return;
      console.error("[AppleSignIn] popup/SDK error:", err);
      onError("Apple sign-in failed. Try again.");
    });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || !ready}
      className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-black px-4 py-2.5 text-sm font-medium text-white transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <svg width="18" height="18" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
      </svg>
      {mode === "signin" ? "Sign in with Apple" : "Sign up with Apple"}
    </button>
  );
};

export default AppleSignInButton;
